// frontend/src/components/Modals/ContentModal/components/BulkVideoWatermark.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_URL, { BASE_URL } from '../../../../config';
import './VideoWatermark.css';

function BulkVideoWatermark({ isOpen, onClose, videoPreviews, onSave }) {
    const [watermarkFile, setWatermarkFile] = useState(null);
    const [watermarkPreview, setWatermarkPreview] = useState(null);
    const [watermarkPosition, setWatermarkPosition] = useState('bottom-right');
    const [watermarkSize, setWatermarkSize] = useState(100);
    const [watermarkOpacity, setWatermarkOpacity] = useState(80);
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);

    // Markalar için state
    const [brands, setBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [selectedBrandLogo, setSelectedBrandLogo] = useState(null);

    const iconInputRef = useRef(null);

    // Markaları yükle
    useEffect(() => {
        if (isOpen) {
            fetchBrands();
        }
    }, [isOpen]);

    const fetchBrands = async () => {
        try {
            setLoadingBrands(true);
            const response = await axios.get(`${API_URL}/brands`);
            const activeBrands = (response.data.brands || []).filter(
                brand => brand.is_active && (brand.logo_dark_url || brand.logo_light_url)
            );
            setBrands(activeBrands);
        } catch (error) {
            console.error('Brands fetch error:', error);
        } finally {
            setLoadingBrands(false);
        }
    };

    // ESC tuşu
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27 && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // Modal kapandığında state'leri sıfırla
    useEffect(() => {
        if (!isOpen) {
            setWatermarkFile(null);
            setWatermarkPreview(null);
            setWatermarkPosition('bottom-right');
            setWatermarkSize(100);
            setWatermarkOpacity(80);
            setSelectedPreviewIndex(0);
            setSelectedBrandLogo(null);
        }
    }, [isOpen]);

    // Icon dosyası seçildiğinde
    const handleIconSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('❌ Sadece resim dosyaları kullanılabilir');
                e.target.value = null;
                return;
            }

            setWatermarkFile(file);
            setSelectedBrandLogo(null);

            const reader = new FileReader();
            reader.onloadend = () => {
                setWatermarkPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Marka logosu seçildiğinde
    const handleBrandLogoSelect = async (brand, logoType) => {
        const logoUrl = logoType === 'dark' ? brand.logo_dark_url : brand.logo_light_url;
        if (!logoUrl) return;

        try {
            const fullUrl = `${BASE_URL}${logoUrl}`;

            const response = await fetch(fullUrl);
            const blob = await response.blob();
            const fileName = logoUrl.split('/').pop();
            const file = new File([blob], fileName, { type: blob.type });

            setWatermarkFile(file);
            setWatermarkPreview(fullUrl);
            setSelectedBrandLogo({ brandId: brand.id, type: logoType });
            // Markanın varsayılan watermark ayarlarını uygula
            if (brand.default_wm_position) setWatermarkPosition(brand.default_wm_position);
            if (brand.default_wm_size) setWatermarkSize(brand.default_wm_size);
            if (brand.default_wm_opacity) setWatermarkOpacity(brand.default_wm_opacity);

            if (iconInputRef.current) {
                iconInputRef.current.value = null;
            }
        } catch (error) {
            console.error('Brand logo select error:', error);
            alert('Logo yüklenirken hata oluştu');
        }
    };

    // Icon kaldır
    const handleRemoveIcon = () => {
        setWatermarkFile(null);
        setWatermarkPreview(null);
        setSelectedBrandLogo(null);
        if (iconInputRef.current) {
            iconInputRef.current.value = null;
        }
    };

    // Tümüne uygula
    const handleApplyToAll = () => {
        if (!watermarkPreview || !watermarkFile) {
            alert('❌ Önce bir logo seçmelisiniz');
            return;
        }

        const watermarkData = {
            file: watermarkFile,
            preview: watermarkPreview,
            position: watermarkPosition,
            size: watermarkSize,
            opacity: watermarkOpacity
        };

        onSave(watermarkData);
        onClose();
    };

    if (!isOpen) return null;

    // Sadece videoları filtrele
    const videoOnlyPreviews = (videoPreviews || []).filter(p => p?.type === 'video');

    if (videoOnlyPreviews.length === 0) {
        return (
            <div className="vw-modal-overlay" onClick={onClose}>
                <div className="vw-modal-container vw-modal-error" onClick={(e) => e.stopPropagation()}>
                    <div className="vw-error-content">
                        <span className="vw-error-icon">📹</span>
                        <p>Seçili medyalar arasında video bulunamadı.</p>
                        <p style={{ fontSize: '13px', marginTop: '8px', color: '#6c7086' }}>Logo eklemek için önce video yükleyin.</p>
                        <button onClick={onClose} className="vw-btn vw-btn-secondary" style={{ marginTop: '16px' }}>Kapat</button>
                    </div>
                </div>
            </div>
        );
    }

    const currentVideo = videoOnlyPreviews[selectedPreviewIndex] || videoOnlyPreviews[0];

    // Pozisyon grid
    const positions = [
        { key: 'top-left', label: 'Sol Üst' },
        { key: 'top-center', label: 'Orta Üst' },
        { key: 'top-right', label: 'Sağ Üst' },
        { key: 'center-left', label: 'Sol Orta' },
        { key: 'center', label: 'Orta' },
        { key: 'center-right', label: 'Sağ Orta' },
        { key: 'bottom-left', label: 'Sol Alt' },
        { key: 'bottom-center', label: 'Orta Alt' },
        { key: 'bottom-right', label: 'Sağ Alt' }
    ];

    const currentPositionLabel = positions.find(p => p.key === watermarkPosition)?.label || 'Sağ Alt';

    return (
        <div className="vw-modal-overlay" onClick={onClose}>
            <div className="vw-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="vw-modal-header">
                    <div className="vw-header-title">
                        <span className="vw-header-icon">🎬</span>
                        <h3>Toplu Video Logo Ekle</h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                            background: 'rgba(137, 180, 250, 0.15)',
                            color: '#89b4fa',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 500
                        }}>
                            {videoOnlyPreviews.length} video
                        </span>
                        <button className="vw-close-btn" onClick={onClose}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="vw-modal-content">
                    {/* Sol Panel - Video Önizleme */}
                    <div className="vw-preview-panel">
                        {/* Video Seçici (birden fazla video varsa) */}
                        {videoOnlyPreviews.length > 1 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '16px',
                                flexWrap: 'wrap'
                            }}>
                                <span style={{ color: '#6c7086', fontSize: '13px' }}>Önizleme:</span>
                                {videoOnlyPreviews.map((video, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => setSelectedPreviewIndex(index)}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '8px',
                                            border: selectedPreviewIndex === index
                                                ? '2px solid #89b4fa'
                                                : '2px solid rgba(255,255,255,0.1)',
                                            background: selectedPreviewIndex === index
                                                ? 'rgba(137, 180, 250, 0.2)'
                                                : 'rgba(255,255,255,0.05)',
                                            color: selectedPreviewIndex === index ? '#89b4fa' : '#a6adc8',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s'
                                        }}
                                        title={video.name || `Video ${index + 1}`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="vw-video-wrapper">
                            <video
                                src={currentVideo?.url}
                                controls
                                className="vw-video"
                                key={selectedPreviewIndex}
                            />

                            {/* Logo Overlay (Önizleme) */}
                            {watermarkPreview && (
                                <div
                                    className={`vw-logo-overlay vw-pos-${watermarkPosition}`}
                                    style={{ opacity: watermarkOpacity / 100 }}
                                >
                                    <img
                                        src={watermarkPreview}
                                        alt="Logo"
                                        style={{
                                            height: `${Math.max(20, watermarkSize * 0.3)}px`,
                                            width: 'auto'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="vw-video-info">
                            📹 {videoOnlyPreviews.length} video seçili • Logo kayıt sırasında eklenecek
                        </div>
                    </div>

                    {/* Sağ Panel - Ayarlar */}
                    <div className="vw-settings-panel">
                        {/* Logo Seçimi */}
                        <div className="vw-section">
                            <div className="vw-section-header">
                                <span className="vw-section-icon">🖼️</span>
                                <span className="vw-section-title">Logo Seç</span>
                            </div>

                            <div className="vw-logo-selector">
                                <input
                                    ref={iconInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleIconSelect}
                                    id="bulk-vw-logo-input"
                                    hidden
                                />

                                {!watermarkPreview ? (
                                    <label htmlFor="bulk-vw-logo-input" className="vw-logo-dropzone">
                                        <div className="vw-dropzone-icon">
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M12 5v14M5 12h14" />
                                            </svg>
                                        </div>
                                        <span className="vw-dropzone-text">Logo seçmek için tıklayın</span>
                                        <span className="vw-dropzone-hint">PNG, JPG, SVG</span>
                                    </label>
                                ) : (
                                    <div className="vw-logo-preview">
                                        <img src={watermarkPreview} alt="Seçilen Logo" />
                                        <button
                                            className="vw-logo-remove"
                                            onClick={handleRemoveIcon}
                                            title="Logoyu Kaldır"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 6L6 18M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Kayıtlı Markalar */}
                        {brands.length > 0 && (
                            <div className="vw-section">
                                <div className="vw-section-header">
                                    <span className="vw-section-icon">🏷️</span>
                                    <span className="vw-section-title">Kayıtlı Markalar</span>
                                </div>

                                <div className="vw-brands-list">
                                    {loadingBrands ? (
                                        <div className="vw-brands-loading">
                                            <span>Yükleniyor...</span>
                                        </div>
                                    ) : (
                                        brands.map(brand => (
                                            <div key={brand.id} className="vw-brand-item">
                                                <span className="vw-brand-name">{brand.name}</span>
                                                <div className="vw-brand-logos">
                                                    {brand.logo_dark_url && (
                                                        <button
                                                            className={`vw-brand-logo-btn dark ${selectedBrandLogo?.brandId === brand.id &&
                                                                    selectedBrandLogo?.type === 'dark' ? 'selected' : ''
                                                                }`}
                                                            onClick={() => handleBrandLogoSelect(brand, 'dark')}
                                                            title="Koyu arka plan logosu"
                                                        >
                                                            <img
                                                                src={`${BASE_URL}${brand.logo_dark_url}`}
                                                                alt={`${brand.name} - Koyu`}
                                                            />
                                                        </button>
                                                    )}
                                                    {brand.logo_light_url && (
                                                        <button
                                                            className={`vw-brand-logo-btn light ${selectedBrandLogo?.brandId === brand.id &&
                                                                    selectedBrandLogo?.type === 'light' ? 'selected' : ''
                                                                }`}
                                                            onClick={() => handleBrandLogoSelect(brand, 'light')}
                                                            title="Açık arka plan logosu"
                                                        >
                                                            <img
                                                                src={`${BASE_URL}${brand.logo_light_url}`}
                                                                alt={`${brand.name} - Açık`}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Pozisyon, Boyut, Opaklık - Logo seçildiyse göster */}
                        {watermarkPreview && (
                            <>
                                {/* Pozisyon */}
                                <div className="vw-section">
                                    <div className="vw-section-header">
                                        <span className="vw-section-icon">📍</span>
                                        <span className="vw-section-title">Pozisyon</span>
                                        <span className="vw-section-value">{currentPositionLabel}</span>
                                    </div>

                                    <div className="vw-position-grid">
                                        {positions.map(pos => (
                                            <button
                                                key={pos.key}
                                                className={`vw-position-btn ${watermarkPosition === pos.key ? 'active' : ''}`}
                                                onClick={() => setWatermarkPosition(pos.key)}
                                                title={pos.label}
                                            >
                                                <span className="vw-position-dot"></span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Boyut */}
                                <div className="vw-section">
                                    <div className="vw-section-header">
                                        <span className="vw-section-icon">📏</span>
                                        <span className="vw-section-title">Boyut</span>
                                        <span className="vw-section-value">{watermarkSize}%</span>
                                    </div>

                                    <div className="vw-slider-container">
                                        <input
                                            type="range"
                                            min="30"
                                            max="200"
                                            value={watermarkSize}
                                            onChange={(e) => setWatermarkSize(Number(e.target.value))}
                                            className="vw-slider"
                                        />
                                        <div className="vw-slider-labels">
                                            <span>Küçük</span>
                                            <span>Büyük</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Opaklık */}
                                <div className="vw-section">
                                    <div className="vw-section-header">
                                        <span className="vw-section-icon">💎</span>
                                        <span className="vw-section-title">Opaklık</span>
                                        <span className="vw-section-value">{watermarkOpacity}%</span>
                                    </div>

                                    <div className="vw-slider-container">
                                        <input
                                            type="range"
                                            min="10"
                                            max="100"
                                            value={watermarkOpacity}
                                            onChange={(e) => setWatermarkOpacity(Number(e.target.value))}
                                            className="vw-slider"
                                        />
                                        <div className="vw-slider-labels">
                                            <span>Şeffaf</span>
                                            <span>Opak</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="vw-modal-footer">
                    <button className="vw-btn vw-btn-secondary" onClick={onClose}>
                        İptal
                    </button>
                    <button
                        className="vw-btn vw-btn-primary"
                        onClick={handleApplyToAll}
                        disabled={!watermarkPreview}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                            <polyline points="17,21 17,13 7,13 7,21" />
                            <polyline points="7,3 7,8 15,8" />
                        </svg>
                        {videoOnlyPreviews.length} Videoya Uygula
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BulkVideoWatermark;